import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Tag, 
  Modal, 
  Form, 
  Input, 
  Select, 
  message, 
  Space, 
  Typography, 
  Row, 
  Col, 
  Statistic,
  Badge,
  Dropdown,
  Menu,
  Avatar,
  Tooltip,
  Switch
} from 'antd';
import { 
  UserOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  EyeOutlined,
  SearchOutlined,
  ReloadOutlined,
  MoreOutlined,
  MailOutlined,
  CalendarOutlined,
  CrownOutlined,
  TeamOutlined,
  UserAddOutlined
} from '@ant-design/icons';
import { 
  collection, 
  query, 
  orderBy, 
  limit,
  onSnapshot, 
  doc, 
  updateDoc, 
  deleteDoc,
  where,
  getDocs
} from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { theme } from '../../../styles/theme';
import { trackAdminEvent, ANALYTICS_EVENTS } from '../../../utils/analytics';
import { formatDate } from '../../../utils/helpers';

const { Title, Text } = Typography;
const { Option } = Select;

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editForm] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [stats, setStats] = useState({
    total: 0,
    admins: 0,
    customers: 0,
    active: 0
  });

  // Fetch users from Firestore with pagination
  useEffect(() => {
    const q = query(
      collection(db, 'users'),
      orderBy('createdAt', 'desc'),
      limit(100) // Limit to 100 most recent users for better performance
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const usersData = [];
      let adminCount = 0;
      let customerCount = 0;
      let activeCount = 0;

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        usersData.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate?.() || new Date(),
          lastLoginAt: data.lastLoginAt?.toDate?.() || null
        });

        // Count stats
        if (data.role === 'admin' || data.isSuperUser) {
          adminCount++;
        } else {
          customerCount++;
        }
        if (data.isActive !== false) {
          activeCount++;
        }
      });

      setUsers(usersData);
      setStats({
        total: usersData.length,
        admins: adminCount,
        customers: customerCount,
        active: activeCount
      });
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Filter users based on search and role
  const filteredUsers = users.filter(user => {
    const matchesSearch = !searchText || 
      user.email?.toLowerCase().includes(searchText.toLowerCase()) ||
      user.name?.toLowerCase().includes(searchText.toLowerCase()) ||
      user.firstName?.toLowerCase().includes(searchText.toLowerCase()) ||
      user.lastName?.toLowerCase().includes(searchText.toLowerCase());
    
    const matchesRole = roleFilter === 'all' || 
      (roleFilter === 'admin' && (user.role === 'admin' || user.isSuperUser)) ||
      (roleFilter === 'customer' && user.role !== 'admin' && !user.isSuperUser);
    
    return matchesSearch && matchesRole;
  });

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setViewModalVisible(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    
    // Determine the role to display in the form
    let displayRole = user.role || 'customer';
    if (user.isSuperUser) {
      displayRole = 'super_admin';
    }
    
    editForm.setFieldsValue({
      role: displayRole,
      isActive: user.isActive !== false,
      permissions: user.permissions || []
    });
    setEditModalVisible(true);
  };

  const handleUpdateUser = async (values) => {
    try {
      // Handle super admin role
      const updateData = { ...values };
      
      if (values.role === 'super_admin') {
        updateData.role = 'admin';
        updateData.isSuperUser = true;
        updateData.permissions = ['analytics', 'admin_dashboard', 'user_management', 'quote_management'];
      } else {
        updateData.isSuperUser = false;
        updateData.permissions = [];
      }
      
      await updateDoc(doc(db, 'users', selectedUser.id), {
        ...updateData,
        updatedAt: new Date()
      });
      
      trackAdminEvent(ANALYTICS_EVENTS.USER_UPDATED, {
        userId: selectedUser.id,
        changes: updateData
      });
      
      const roleText = values.role === 'super_admin' ? 'Super Admin' : values.role;
      message.success(`User updated successfully to ${roleText}`);
      setEditModalVisible(false);
      setSelectedUser(null);
      editForm.resetFields();
    } catch (error) {
      console.error('Error updating user:', error);
      message.error('Failed to update user');
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await deleteDoc(doc(db, 'users', userId));
      
      trackAdminEvent(ANALYTICS_EVENTS.USER_DELETED, {
        userId
      });
      
      message.success('User deleted successfully');
    } catch (error) {
      console.error('Error deleting user:', error);
      message.error('Failed to delete user');
    }
  };

  const handleToggleUserStatus = async (userId, isActive) => {
    try {
      await updateDoc(doc(db, 'users', userId), {
        isActive: !isActive,
        updatedAt: new Date()
      });
      
      trackAdminEvent(ANALYTICS_EVENTS.USER_STATUS_CHANGED, {
        userId,
        newStatus: !isActive
      });
      
      message.success(`User ${!isActive ? 'activated' : 'deactivated'} successfully`);
    } catch (error) {
      console.error('Error updating user status:', error);
      message.error('Failed to update user status');
    }
  };

  const getRoleColor = (user) => {
    if (user.isSuperUser) return 'red';
    if (user.role === 'admin') return 'orange';
    return 'blue';
  };

  const getRoleIcon = (user) => {
    if (user.isSuperUser) return <CrownOutlined />;
    if (user.role === 'admin') return <UserOutlined />;
    return <TeamOutlined />;
  };

  const columns = [
    {
      title: 'User',
      key: 'user',
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Avatar 
            size={40} 
            icon={<UserOutlined />}
            style={{ backgroundColor: theme.colors.primary }}
          />
          <div>
            <div style={{ fontWeight: 'bold', color: theme.colors.black }}>
              {record.name || record.firstName || record.email}
            </div>
            <div style={{ fontSize: '12px', color: theme.colors.gray }}>
              {record.email}
            </div>
          </div>
        </div>
      )
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (_, record) => (
        <Tag 
          color={getRoleColor(record)} 
          icon={getRoleIcon(record)}
          style={{ fontFamily: theme.fonts.primary }}
        >
          {record.isSuperUser ? 'Super Admin' : (record.role === 'admin' ? 'Admin' : 'Customer')}
        </Tag>
      )
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive, record) => (
        <Switch
          checked={isActive !== false}
          onChange={() => handleToggleUserStatus(record.id, isActive)}
          checkedChildren="Active"
          unCheckedChildren="Inactive"
        />
      )
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => (
        <Text style={{ fontFamily: theme.fonts.primary }}>
          {formatDate(date)}
        </Text>
      )
    },
    {
      title: 'Last Login',
      dataIndex: 'lastLoginAt',
      key: 'lastLoginAt',
      render: (date) => (
        <Text style={{ fontFamily: theme.fonts.primary }}>
          {date ? formatDate(date) : 'Never'}
        </Text>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleViewUser(record)}
              style={{ color: theme.colors.primary }}
            />
          </Tooltip>
          <Tooltip title="Edit User">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEditUser(record)}
              style={{ color: theme.colors.primary }}
            />
          </Tooltip>
          <Dropdown
            menu={{
              items: [
                {
                  key: 'delete',
                  label: 'Delete User',
                  icon: <DeleteOutlined />,
                  danger: true,
                  onClick: () => {
                    Modal.confirm({
                      title: 'Delete User',
                      content: `Are you sure you want to delete ${record.name || record.email}?`,
                      okText: 'Delete',
                      okType: 'danger',
                      onOk: () => handleDeleteUser(record.id)
                    });
                  }
                }
              ]
            }}
            trigger={['click']}
          >
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        </Space>
      )
    }
  ];

  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto', width: '100%' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <Title level={2} style={{ 
          color: theme.colors.black, 
          fontFamily: theme.fonts.primary,
          marginBottom: '8px'
        }}>
          <TeamOutlined /> User Management
        </Title>
        <Text style={{ color: theme.colors.gray, fontFamily: theme.fonts.primary }}>
          Manage user accounts, roles, and permissions
        </Text>
      </div>

      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic
              title="Total Users"
              value={stats.total}
              prefix={<UserOutlined style={{ color: theme.colors.primary }} />}
              valueStyle={{ color: theme.colors.primary }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic
              title="Admins"
              value={stats.admins}
              prefix={<CrownOutlined style={{ color: '#fa8c16' }} />}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic
              title="Customers"
              value={stats.customers}
              prefix={<TeamOutlined style={{ color: '#1890ff' }} />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic
              title="Active Users"
              value={stats.active}
              prefix={<UserOutlined style={{ color: '#52c41a' }} />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Controls */}
      <Card style={{ marginBottom: '24px' }}>
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Input.Search
              placeholder="Search users by name or email..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: '100%', maxWidth: '400px' }}
              prefix={<SearchOutlined />}
            />
          </Col>
          <Col>
            <Select
              value={roleFilter}
              onChange={setRoleFilter}
              style={{ width: 150 }}
            >
              <Option value="all">All Roles</Option>
              <Option value="admin">Admins</Option>
              <Option value="customer">Customers</Option>
            </Select>
          </Col>
          <Col>
            <Button 
              icon={<ReloadOutlined />}
              onClick={() => window.location.reload()}
            >
              Refresh
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Users Table */}
      <Card 
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <TeamOutlined style={{ color: theme.colors.primary }} />
            <span style={{ fontFamily: theme.fonts.primary }}>Users</span>
            <Badge count={filteredUsers.length} style={{ backgroundColor: theme.colors.primary }} />
          </div>
        }
      >
        <Table
          columns={columns}
          dataSource={filteredUsers}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} users`
          }}
        />
      </Card>

      {/* View User Modal */}
      <Modal
        title="User Details"
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={null}
        width={600}
      >
        {selectedUser && (
          <div>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                  <Avatar 
                    size={80} 
                    icon={<UserOutlined />}
                    style={{ backgroundColor: theme.colors.primary }}
                  />
                  <Title level={4} style={{ marginTop: '16px', marginBottom: '4px' }}>
                    {selectedUser.name || selectedUser.firstName || selectedUser.email}
                  </Title>
                  <Tag 
                    color={getRoleColor(selectedUser)} 
                    icon={getRoleIcon(selectedUser)}
                  >
                    {selectedUser.isSuperUser ? 'Super Admin' : (selectedUser.role === 'admin' ? 'Admin' : 'Customer')}
                  </Tag>
                </div>
              </Col>
              <Col span={12}>
                <Text strong>Email:</Text>
                <br />
                <Text>{selectedUser.email}</Text>
              </Col>
              <Col span={12}>
                <Text strong>Status:</Text>
                <br />
                <Tag color={selectedUser.isActive !== false ? 'green' : 'red'}>
                  {selectedUser.isActive !== false ? 'Active' : 'Inactive'}
                </Tag>
              </Col>
              <Col span={12}>
                <Text strong>Created:</Text>
                <br />
                <Text>{formatDate(selectedUser.createdAt)}</Text>
              </Col>
              <Col span={12}>
                <Text strong>Last Login:</Text>
                <br />
                <Text>{selectedUser.lastLoginAt ? formatDate(selectedUser.lastLoginAt) : 'Never'}</Text>
              </Col>
              {selectedUser.permissions && (
                <Col span={24}>
                  <Text strong>Permissions:</Text>
                  <br />
                  <Space wrap>
                    {selectedUser.permissions.map(permission => (
                      <Tag key={permission} color="blue">{permission}</Tag>
                    ))}
                  </Space>
                </Col>
              )}
            </Row>
          </div>
        )}
      </Modal>

      {/* Edit User Modal */}
      <Modal
        title="Edit User"
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          setSelectedUser(null);
          editForm.resetFields();
        }}
        onOk={() => editForm.submit()}
        width={500}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleUpdateUser}
        >
          <Form.Item
            name="role"
            label="Role"
            rules={[{ required: true, message: 'Please select a role' }]}
          >
            <Select>
              <Option value="customer">Customer</Option>
              <Option value="admin">Admin</Option>
              <Option value="super_admin">Super Admin</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="isActive"
            label="Status"
            valuePropName="checked"
          >
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UserManagement;
